import cloneDeep from 'lodash.clonedeepwith'
import intersection from 'lodash.intersectionwith'
import isEqual from 'lodash.isequal'
import times from 'lodash.times'
import { ClosingSequence, OpeningSequence, SequenceLink } from './sequences'
import {
  DuplicateParserConfigVersionError,
  ParserConfigNotFoundError,
  UnableToRepresentAsMarkupError,
  UnsupportedElementTypeError,
  UnsupportedNodeClassError
} from './exceptions'
import {
  Element,
  MarkupOption,
  Option,
  ParserConfig,
  Text,
  TextOptionMapping
} from './models'


class Parser {

  static escapeText(line, charsToEscape) {
    let posStart = 0
    let posIndex = 0
    let newCharsToEscape = new Set([ Parser.CHAR_ESCAPE, ...charsToEscape ])
    let newLine = []
    while (posIndex < line.length) {
      if (newCharsToEscape.has(line[posIndex])) {
        // Skip the escape character
        newLine.push(line.slice(posStart,posIndex))
        newLine.push(Parser.CHAR_ESCAPE)
        posStart = posIndex
      }
      posIndex += 1
    }
    if (posStart < line.length) {
      newLine.push(line.slice(posStart))
    }
    return newLine.join('')
  }

  static escapedSplit(line, splitChar, unescape = false) {
    let posStart = 0
    let posIndex = 0
    let ret = []
    let newLine = [ '' ]
    while (posIndex < line.length) {
      if (line[posIndex] === Parser.CHAR_ESCAPE) {
        newLine.push(line.slice(posStart, posIndex))
        posStart = posIndex
        // Jump ahead one extra so the next char isn't processed
        posIndex += 1
        if (unescape) {
          // Don't include this escape char in the output
          posStart += 1
        }
      } else if (line[posIndex] === splitChar) {
        newLine.push(line.slice(posStart, posIndex))
        ret.push(newLine.join(''))
        newLine = [ '' ]
        posStart = posIndex + 1
      }
      posIndex += 1
    }
    if (posStart < line.length) {
      newLine.push(line.slice(posStart))
      ret.push(newLine.join(''))
    }

    return ret.length === 0 ? [ '' ] : ret
  }

  static filterOutSequencesInOptions(sortedSequences) {
    /*
    This takes in a list of paired, unfiltered sequences that are in order of appearance in the markup.
    It returns a list of paired, filtered sequences that are in order of appearance in the markup.

    Filtering:
      Locates and discards paired sequences contained within an options sequence.
    Example: { ... }( ... { ... } ... )
                          ^     ^
                          These are discarded since they're within an options sequence.
    */
    let isOption = false
    let lastSequence = null
    let sequences = []
    sortedSequences && sortedSequences.forEach(sequence => {
      if (sequence.converter.isOption) {
        if (sequence.isOpening) {
          if (lastSequence !== null && lastSequence.pos + 1 === sequence.pos) {
            sequences.push(sequence)
            isOption = true
          }
        } else {
          sequences.push(sequence)
          isOption = false
        }
      } else if (!isOption) {
        sequences.push(sequence)
      }
      lastSequence = sequence
    })
    return sequences
  }

  static getChildDepth(line) {
    // Determines how many tabs are at the beginning of the line.
    let depth = 0
    // We don't mess around with escape characters since the escape character isn't a tab and would stop the depth
    // count without any special handling
    while (depth < line.length && line[depth] === Parser.CHAR_CHILD) {
      depth += 1
    }
    return depth
  }

  static getMarkupOptionByText(textOptionMappings) {
    /*
    It's purpose is to try and reduce repeated option data by determining subsets of the markup
    that can be enclosed in a single sequence instead of the way-too-verbose default way of applying options
    directly on each text node. This obviously adds more time and complexity to the function, but it can save MANY
    characters from the generated markup, and since one of the main purposes of markup is its relatively compact
    size, the trade-off is worth it.

    This function uses an algorithm that makes a decent guess at the most optimal way to stack the options and
    operates in O(NlogN) time complexity. There's another algorithm that would more or less guarantee the most
    optimal way, but it operates in O(N/2(N)), so it doesn't scale very well. With the more accurate algorithm we
    could introduce a configurable max depth to stop it from going too far down the rabbit hole on larger data sets,
    but one of the main things was to optimize was data that's applied across the entire paragraph, like a
    font and font size. If we used the more accurate algorithm with a max depth, it would prioritize the less wide
    spread common data whereas this estimation algorithm is still very sensitive to wide spread common data.

    The algorithm works by using a divide-and-conquer strategy where we look at every text pair, and move their
    common option data "up a level" and remove it from the lower level. We then repeat with the next level until we
    have a level with only 1 option datum OR we didn't find any common option data in the last level. We then store
    this data in an easily searchable data structure and return it.

    It also covers some additional edge cases, and other optimizations.
    */
    let markupOptionByText = new Map()
    let depth = 0
    let textOptionMappingDepth = [ textOptionMappings.map(t => t.data) ]

    while (textOptionMappingDepth[depth].length > 1) {
      let depthList = []
      let empty = true
      for (let i = 0; i < textOptionMappingDepth[depth].length; i++) {
        if (i % 2 === 0) { // even index
          let leftData = textOptionMappingDepth[depth][i]
          let sameData = {}
          if (textOptionMappingDepth[depth].length > i + 1) {
            let rightData = textOptionMappingDepth[depth][i + 1]
            const sameKeys = intersection(Object.keys(leftData), Object.keys(rightData))
            for (const keyIndex in sameKeys) {
              // If they have the same key but different values, we'll arbitrarily pick one side to move up
              // and leave the other side at this level.
              // Doing this provides a chance for something like:
              // { ... { ... }(size:14) ... }(size:15)
              // instead of guaranteeing something like:
              // { ... }(size:15){ ... }(size:14){ ... }(size:15)
              empty = false
              const sameKey = sameKeys[keyIndex]
              sameData[sameKey] = leftData[sameKey]
              if (leftData[sameKey] === rightData[sameKey]) {
                // If they have the same key AND value, then move both sides up
                delete rightData[sameKey]
              }
              delete leftData[sameKey]
            }
          } else {
            empty = false
            sameData = cloneDeep(leftData)
            textOptionMappingDepth[depth][i] = {}
          }
          depthList.push(sameData)
        }
      }
      if (empty) break
      textOptionMappingDepth.push(depthList)
      depth += 1
    }

    for (let i = depth; i > -1; i--) {
      const batchSize = 2 ** i
      for (let j = 0; j < textOptionMappingDepth[i].length; j++) {
        const startIndex = j * batchSize
        const newTextOptionMappings = textOptionMappings.slice(startIndex, startIndex + batchSize)
        const data = textOptionMappingDepth[i][j]
        let groups = []
        if (Object.keys(data).length === 0) {
          continue
        }
        if (newTextOptionMappings[0].parent === newTextOptionMappings[newTextOptionMappings.length - 1].parent) {
          // No splitting required.
          // Covers cases like:
          // t1 ... tn | t1 ... [ ... ] ... tn | t1
          groups = [ newTextOptionMappings ]
        } else {
          // Not all text nodes in this range roll up to the same parent.
          // Splitting is required to avoid a case where we try to apply a text option between 2 or more
          // partial inline elements.
          // Covers cases like:
          // t1 ... [ ... tn ... ] | [ ... t1 ... ] ... [ ... ] ... [ ... tn ... ]
          const appendToGroups = (_group, _groupStart) => {
            if (group && group.length > 0) {
              if (group.length < batchSize && (group.length % 2 === 0 || group.length === 1)) {
                // Not _required_, but you get some dumb looking markup if you don't do this
                // Example of dumb markup: {{some text}(bold)}(italic)
                const tempDepth = parseInt(Math.log2(group.length))
                const dataIndex = parseInt(_groupStart / group.length)
                textOptionMappingDepth[tempDepth][dataIndex] = {
                  ...data,
                  ...textOptionMappingDepth[tempDepth][dataIndex]
                }
              } else {
                groups.push(group)
              }
            }
          }

          let group = []
          let prevParent = null
          let groupStart = 0
          for (let k = 0; k < newTextOptionMappings.length; k++) {
            const textOptionMapping = newTextOptionMappings[k]
            if (textOptionMapping.parent === prevParent) {
              group.push(textOptionMapping)
            } else {
              appendToGroups(group, groupStart + startIndex)
              prevParent = textOptionMapping.parent
              groupStart = k
              group = [ textOptionMapping ]
            }
          }
          appendToGroups(group, groupStart + startIndex)
        }

        groups && groups.forEach(g => {
          // First textOptionMapping
          let text = g[0].text
          if (!markupOptionByText.has(text)) {
            markupOptionByText.set(text, new MarkupOption())
          }
          markupOptionByText.get(text).optionBeginnings.push({})

          // Last textOptionMapping
          text = g[g.length - 1].text
          if (!markupOptionByText.has(text)) {
            markupOptionByText.set(text, new MarkupOption())
          }
          markupOptionByText.get(text).optionEndings.push({ data })
        })
      }
    }
    return markupOptionByText
  }

  static getMarkupVersion(rtm) {
    // Returns the version of the markup or null if a version isn't present.
    // Versioned markup begins with the pattern 'v[0-9]+\\.'
    const match = /^v([0-9]+)\./.exec(rtm)
    if (match && match.length > 1) {
      const version = parseInt(match[1])
      return Number.isNaN(version) ? null : version
    }
    return null
  }

  static mergeIdenticalTextInElement(children) {
    // Returns a subset of the provided list where adjacent text nodes with the same parent and the same option data
    // are merged together into a single text node.
    let newChildren = []
    let lastChild = null
    for (let i = 0; i < children.length; i++) {
      const child = children[i]
      if (child instanceof Element) {
        child.children = Parser.mergeIdenticalTextInElement(child.children)
      } else if (child instanceof Text && lastChild instanceof Text && isEqual(child.data, lastChild.data)) {
        lastChild.text = `${lastChild.text}${child.text}`
        continue
      }
      lastChild = child
      newChildren.push(child)
    }
    return newChildren
  }

  static pairSequences(sequences) {
    /*
    This takes in a list of unpaired, unfiltered sequences that are in order of appearance in the markup.
    It returns a list of paired, semi-filtered sequences that are in order of appearance in the markup.

    Pairing:
      Each open sequence has a valid closing sequence (and vice-versa). A sequence link is added to each
    sequence with a pointer to its other half.
      Pairing happens in a greedy approach, so the first valid closing sequence is paired to an open sequence.
    Example 1: { ... } ... }
               ^     ^
               These are matched and made a pair
               { ... } ... }
                           ^
                           This is discarded since there's no open sequence
    Example 2: { ... [ ... } ... ]
               ^           ^
               These are matched and made a pair
               { ... [ ... } ... ]
                     ^
                     This is discarded since it's within a pair and hasn't been paired itself
               { ... [ ... } ... ]
                                 ^
                                 This is discarded since there's no open sequence (the open sequence was discarded)

    Filtering:
      Doesn't pair option sequences that aren't adjacent to a paired text or element sequence.
    Example 1: { ... }( ... )
               ^     ^
               These are matched and made a pair
               { ... }( ... )
                      ^     ^
                      These are matched and made a pair since they're adjacent to a paired text sequence
    Example 2: ... }( ... )
                   ^
                   This is discarded since there's no open sequence
               ... }( ... )
                    ^     ^
                    These are discarded since they're not adjacent to a paired text or element sequence
    */
    let openStack = []
    let completeSequences = []
    let lastMatchedSequence = null
    for (let i = 0; i < sequences.length; i++) {
      const sequence = sequences[i]
      if (sequence.isOpening) {
        if (sequence.converter.isOption && (lastMatchedSequence === null || lastMatchedSequence.pos + 1 !== sequence.pos)) {
          // This option doesn't have a valid element or text sequence to adhere to
          continue
        }
        openStack.push(sequence)
      } else {
        let match = false
        let poppedSequences = []
        while (openStack.length > 0) {
          const openSequence = openStack.pop()
          if (openSequence.converter === sequence.converter) {
            // Match
            match = true
            const link = new SequenceLink(openSequence, sequence)
            openSequence.link = link
            sequence.link = link
            completeSequences.push(openSequence)
            completeSequences.push(sequence)
            if (!sequence.converter.isOption) {
              lastMatchedSequence = sequence
            } else {
              lastMatchedSequence = null
            }
            break
          } else {
            // No match
            poppedSequences.push(openSequence)
          }
        }
        if (!match) {
          // Reset openStack
          poppedSequences.reverse()
          openStack = [ ...openStack, ...poppedSequences ]
        }
      }
    }
    completeSequences.sort((a, b) => a.pos - b.pos)
    return completeSequences
  }

  static removeEmptyTextInElement(children) {
    // Returns a subset of the provided list of text and inline element nodes and removes any text nodes with no text.
    // If the subset becomes an empty list, an empty text node is added in to comply with the AST schema.
    let newChildren = []
    for (let i = 0; i < children.length; i++) {
      const child = children[i]
      if (child instanceof Element) {
        child.children = Parser.removeEmptyTextInElement(child.children)
      } else if (child.text === '') {
        continue
      }
      newChildren.push(child)
    }
    if (newChildren.length === 0) {
      newChildren.push(Parser.BLANK_TEXT)
    }
    return newChildren
  }

  static unescapeText(line) {
    let posStart = 0
    let posIndex = 0
    let newLine = []
    while (posIndex < line.length) {
      if (line[posIndex] === Parser.CHAR_ESCAPE) {
        newLine.push(line.slice(posStart, posIndex))
        posStart = posIndex + 1
        posIndex += 1
      }
      posIndex += 1
    }
    if (posStart < line.length) {
      newLine.push(line.slice(posStart))
    }
    return newLine.join('')
  }

  constructor(inputParserConfigs) {
    const parserConfigs = inputParserConfigs ? inputParserConfigs : [ new ParserConfig() ]
    this.parserConfigByVersion = {}
    parserConfigs.forEach(parserConfig => {
      if (parserConfig.version in this.parserConfigByVersion) {
        throw new DuplicateParserConfigVersionError(parserConfig.version)
      }
      this.parserConfigByVersion[parserConfig.version] = parserConfig
    })
    this.newestVersion = Math.max(...Object.keys(this.parserConfigByVersion))
  }

  astToNodes(ast) {
    // Converts from the AST representation to the node representation.
    // The representations are effectively the same but the AST is JSON serializable.
    let nodes = []
    ast && ast.forEach(node => {
      if (node.children) {
        // Element
        nodes.push(new Element(node.type, this.astToNodes(node.children), node.data))
      } else {
        // Text
        nodes.push(new Text(node.text, node.data))
      }
    })
    return nodes
  }

  markupToNodes(rtm) {
    // This is the main parsing method.
    // This will take in any string and return a list of block elements (nodes).
    // This method should never fail to turn any string into nodes as any string is valid RTM.
    let nodes = [] // The list of block elements to be returned
    let parents = [] // The list of parent block elements in the event has_indent_children are being used

    // Get the markup version so we know which parser config to use.
    // If no version is found, use the newest parser.
    // If an invalid version is found, use the newest parser and treat the version string as part of the markup.
    let markup = rtm
    let parserConfig
    const version = Parser.getMarkupVersion(rtm)
    if (version === null) {
      parserConfig = this.getParserConfig(this.newestVersion)
    } else {
      try {
        parserConfig = this.getParserConfig(version)
        const versionLength = `v${version}.`.length
        markup = rtm.substring(versionLength)
      } catch (e) {
        if (e instanceof ParserConfigNotFoundError) {
          parserConfig = this.getParserConfig(this.newestVersion)
        }
      }
    }

    // Block elements are separated by new lines, but a new line could be text if it's escaped.
    // We make use of the escaped_split method to ignore splitting the string on escaped new lines.
    const lines = Parser.escapedSplit(markup, Parser.CHAR_BLOCK)
    lines.forEach(line => {
      // Keeps track of whether we've appended this to the preceding block element
      // We only do that if the block elements have has_indent_children set to true, the same element type and
      // there's a reasonable amount of tabs (i.e. tabbed no more than 1 additional time from the parent)
      let appendedToParent = false
      // Determine the number of tabs used to start this line, if the block element has has_indent_children set to true,
      // then this will be the N + 1 position of where the child's parent should be located in the parents list
      let childDepth = Parser.getChildDepth(line)
      // Gets the element converter for this line. Default is a paragraph.
      // Takes into account tabs and hasIndentChildren property.
      let elementConverter = this.getBlockElementConverter(parserConfig, line)

      if (childDepth > 0 && (parents.length <= childDepth - 1 || parents[childDepth - 1].type !== elementConverter.type)) {
        // This is a block element that could be a child, but it doesn't have a valid parent.
        // It doesn't have a valid parent because there's no parent at position N - 1 (i.e. tabbed too far) OR
        // The parent doesn't share the same element type.
        // We'll instead change this to a paragraph.
        childDepth = 0
        elementConverter = ParserConfig.ELEMENT_CONVERTER_PARAGRAPH
      }

      // Do all the heavy lifting to create the inline element and text nodes along with their option data.
      const element = this.parseBlockElement(parserConfig, line, elementConverter)
      if (elementConverter.markupIdentifier.hasIndentChildren) {
        /*
        Delete all potential parent elements from child_depth onward and then add ourselves.
        Example: We're handling data like so:
        -line1
        \t-line2
        \t\t-line3
        \t-line4
        \t\t\t-line5

        With the first line, we hose the empty list and add "line1" as the parent at depth 0.
        result: parents = [ line1 ]

        Then with line 2 and line 3, we again hose the non-existent part of the list and then add "line2" and
        "line3" to depths 1 and 2.
        result: parents = [ line1, line2, line3 ]

        Then with line 4 we're back at depth 1, which is totally valid. We hose the list starting from depth
        1 and then add "line4". This effectively replaces "line2" with "line4" for the current parent at depth
        1 and then removes "line3" from being a potential parent.
        result: parents = [ line1, line4 ]

        Finally with line 5, we see that for its depth of 3 there's no valid parent at depth 2. This case is
        caught up above and line 5 becomes a paragraph element.
        */
        parents = parents.slice(0, childDepth)
        parents.push(element)
        if (childDepth > 0) {
          // Append to the parent element
          appendedToParent = true
          parents[childDepth - 1].children.push(element)
        }
      }
      // If we haven't appended this to a parent, append it to the top level list
      if (!appendedToParent) {
        nodes.push(element)
      }
    })
    return nodes
  }

  markupVersion(rtm) {
    // This method returns the version number of the markup
    const version = Parser.getMarkupVersion(rtm)
    return (version in this.parserConfigByVersion) ? version : this.newestVersion
  }

  nodesToAst(nodes, version = null) {
    // Converts from the node representation to the AST representation.
    // The representations are effectively the same but the AST is JSON serializable.
    const updatedVersion = version === null ? this.newestVersion : version
    const parserConfig = this.getParserConfig(updatedVersion)
    return this.nodesToAstInternal(nodes, parserConfig)
  }

  nodesToMarkup(nodes, version = null) {
    // This is the main unparsing method.
    // This will take in any list of block elements (nodes) and return a string.
    // This method will error if the specified version parser doesn't contain
    // element converters for any of the element types provided.
    const updatedVersion = version === null ? this.newestVersion : version
    const parserConfig = this.getParserConfig(updatedVersion)
    const rtm = this.unparseChildren(parserConfig, nodes, 0, new Map(),true, null)
    // Do not version empty markup
    if (rtm.length > 0) {
      return `v${parserConfig.version}.${rtm}`
    }
    return rtm
  }

  nodesToPlainText(nodes, version = null) {
    // Similar to the `nodes_to_markup` method except it only unparses the text values.
    // All option data and patterns are omitted from the output.
    const updatedVersion = version === null ? this.newestVersion : version
    const parserConfig = this.getParserConfig(updatedVersion)
    return this.plainText(parserConfig, nodes, true)
  }

  /* PRIVATE METHODS */
  flattenElement(parserConfig, element) {
    /*
    "Flattens" the text node children of the provided element into a single list.
      Example: An element with the following children
               [T1, E1(T2), T3]
               Where Tn are text nodes and En are element nodes, would return
               [T1, T2, T3]
    The text nodes are wrapped in TextOptionMapping objects that keeps the association to their parent element.
    */
    let textOptionMappings = []
    element.children && element.children.forEach(child => {
      if (child instanceof Element) {
        if (child.type in parserConfig.blockElementsByType) {
          // Block element
        } else {
          // Inline element
          textOptionMappings = [
            ...textOptionMappings,
            ...this.flattenElement(parserConfig, child)
          ]
        }
      } else if (child instanceof Text) {
        textOptionMappings.push(new TextOptionMapping(child, element))
      }
    })
    return textOptionMappings
  }

  getBlockElementConverter(parserConfig, line) {
    // Given a line, determines the block element type and returns the converter.
    // If the line starts with tabs and the first character after the tabs is the open pattern for a block element,
    // that element will be picked so long as the block element has has_indent_children set to True.
    // If no block element can be found, the default paragraph block element is used.
    let elementConverter = ParserConfig.ELEMENT_CONVERTER_PARAGRAPH

    // Don't use the unescaped line since the first character could be an escaped tab
    let indented = false
    let firstChar = null
    if (line.substring(0, 1) === Parser.CHAR_ESCAPE && line.substring(0, 2) !== Parser.DOUBLE_ESCAPE) {
      firstChar = null
    } else if (line.substring(0, 1) === Parser.CHAR_CHILD) {
      firstChar = Parser.unescapeText(line.replace(/^(\t)*/g, '').substring(0, 2)).substring(0, 1)
      indented = true
    } else {
      firstChar = Parser.unescapeText(line.substring(0, 2)).substring(0, 1)
    }

    if (firstChar in parserConfig.blockElementsByOpening) {
      // Look for a block element with the beginning pattern
      const tempConverter = parserConfig.blockElementsByOpening[firstChar]
      if (!indented || tempConverter.markupIdentifier.hasIndentChildren) {
        elementConverter = tempConverter
      }
    }
    return elementConverter
  }

  getSequences(parserConfig, line) {
    // This goes through each character in a line of markup and returns a list of open and closing sequences for each
    // open and closing pattern it detects. It respects the escape character and won't create sequences for escaped
    // open and closing patterns.
    // At this stage, the returned sequences aren't necessarily valid. They'll need to be paired and filtered.
    // NOTE: Because this has to process every character in the line, every operation that can be avoided makes a
    // noticeable improvement to speed.
    let sequences = []

    function addOptionOpeningSequence(converter, index, optionConverter) {
      if (index < line.length
        && converter.markupIdentifier.hasOptions
        && line[index] === optionConverter.markupIdentifier.openPattern) {
        sequences.push(new OpeningSequence(optionConverter, index))
      }
    }

    let pos = 0
    while (pos < line.length) {
      if (line[pos] === Parser.CHAR_ESCAPE) {
        pos += 2
        continue
      }
      const segment = line[pos]
      // This innocent little if statement here is a major time saver. By limiting the number of operations on a
      // majority of the characters in the `line`, testing showed an improvement of ~66%.
      if (parserConfig.possibleMatches.has(segment)) {
        if (segment === parserConfig.optionConverter.markupIdentifier.closePattern) {
          sequences.push(new ClosingSequence(parserConfig.optionConverter, pos))
        }
        if (segment in parserConfig.inlineElementsByClosing) {
          const converter = parserConfig.inlineElementsByClosing[segment]
          sequences.push(new ClosingSequence(converter, pos))
          addOptionOpeningSequence(converter, pos + 1, parserConfig.optionConverter)
        }
        if (segment === parserConfig.textConverter.markupIdentifier.closePattern) {
          sequences.push(new ClosingSequence(parserConfig.textConverter, pos))
          addOptionOpeningSequence(parserConfig.textConverter, pos + 1, parserConfig.optionConverter)
        }
        if (segment in parserConfig.inlineElementsByOpening) {
          sequences.push(new OpeningSequence(parserConfig.inlineElementsByOpening[segment], pos))
        }
        if (segment === parserConfig.textConverter.markupIdentifier.openPattern) {
          sequences.push(new OpeningSequence(parserConfig.textConverter, pos))
        }
      }
      pos += 1
    }
    return sequences
  }

  getParserConfig(version) {
    // Returns the parser config for the given version.
    // Raises a ParserConfigNotFoundError if there's no parser config for the given version.
    const parserConfig = this.parserConfigByVersion[version]
    if (!parserConfig) {
      throw new ParserConfigNotFoundError(version)
    }
    return parserConfig
  }

  nodesToAstInternal(nodes, parserConfig) {
    // This is the recursive function that converts the nodes representation to the AST representation.
    // This is a separate method from the public nodes_to_ast simply because we want to avoid loading the
    // parser config on every recursive call.
    let ast = []
    nodes && nodes.forEach(node => {
      if (node instanceof Element) {
        if ((node.type in parserConfig.blockElementsByType) || (node.type in parserConfig.inlineElementsByType)) {
          ast.push({
            children: this.nodesToAstInternal(node.children, parserConfig),
            data: node.data,
            type: node.type
          })
        } else {
          throw new UnsupportedElementTypeError(node.type)
        }
      } else if (node instanceof Text) {
        ast.push({
          data: node.data,
          text: node.text
        })
      } else {
        throw new UnsupportedNodeClassError(typeof node)
      }
    })
    return ast
  }

  parseBlockElement(parserConfig, line, elementConverter) {
    /*
    Creates a block element from a line of markup.
    The beginning of a block element line can contain special characters which should be processed and then removed
    from the "child" line. The "child" line is the characters that should be processed to form the children nodes
    for this block element.

    First characters we strip:
      - Any tabs IFF the element converter specifies has_indent_children
      - The opening pattern for this block element
      - An options sequence IFF the element converter specifies has_options
    */
    const element = new Element(elementConverter.type, [])
    let newLine
    // First strip away the special characters at the beginning of the block element
    const patternLength = elementConverter.markupIdentifier.openPattern.length
    if (elementConverter.markupIdentifier.hasIndentChildren) {
      newLine = line.replace(/^(\t)*/g, '').substring(patternLength)
    } else {
      newLine = line.substring(patternLength)
    }

    // Then look for an options sequence at the beginning and strip that away from the children
    if (elementConverter.markupIdentifier.hasOptions) {
      const { options, endIndex } = this.parseBlockElementOptions(parserConfig, newLine)
      element.data = options
      if (endIndex > 0) {
        newLine = newLine.substring(endIndex + 1)
      }
    }
    element.children = this.parseBlockElementChildren(parserConfig, newLine)
    return element
  }

  parseBlockElementChildren(parserConfig, line) {
    // Creates a list of children nodes for a block element.
    const sortedSequences = this.parseSequences(parserConfig, line)
    this.parseOptions(line, sortedSequences)
    let children = this.parseInlineElement(line, 0, sortedSequences)
    children = Parser.removeEmptyTextInElement(children)
    children = Parser.mergeIdenticalTextInElement(children)
    return children
  }

  parseBlockElementOptions(parserConfig, line) {
    // Finds the option sequence for a block element (if exists) and parses it.
    // Returns a tuple with the options and the position of the closing pattern in the option sequence
    let options = {}
    let endIndex = -1
    if (line.substring(0, 1) === parserConfig.optionConverter.markupIdentifier.openPattern) {
      let index = 1
      while (index < line.length) {
        if (line[index] === Parser.CHAR_ESCAPE) {
          index += 1
        } else if (line[index] === parserConfig.optionConverter.markupIdentifier.closePattern) {
          endIndex = index
          options = this.parseOption(line.substring(1, endIndex))
          break
        }
        index += 1
      }
    }
    return { options, endIndex }
  }

  parseInlineElement(line, lineDelta, sortedSequences, activeTextOptions = []) {
    // Creates the text and inline element nodes and returns them.

    function posDelta(pos) {
      return pos - lineDelta
    }

    function getActiveTextOptionData() {
      let ret = {}
      // Because active_text_option is being used as a stack, the least specific option is at index 0 so the more
      // specific the option, the higher the index. Traversing with a for loop will cause the more specific options
      // to override duplicate properties of less specific options.
      activeTextOptions && activeTextOptions.forEach(activeTextOption => {
        ret = { ...ret, ...activeTextOption.data }
      })
      return ret
    }

    let children = []
    let startOfFirstSequence

    // Retrieve the non-formatted text at the beginning of the element
    if (sortedSequences && sortedSequences.length > 1) {
      startOfFirstSequence = posDelta(sortedSequences[0].pos)
    } else {
      startOfFirstSequence = line.length
    }
    const beginningText = new Text(
      Parser.unescapeText(line.substring(0, startOfFirstSequence)),
      getActiveTextOptionData()
    )
    children.push(beginningText)

    let elementOpen = 0
    let elementSequence = null
    let elementChildSequences = []
    let lastSequence = null
    sortedSequences && sortedSequences.forEach(sequence => {
      // If we're not assembling children for an element,
      // then produce a new text node between this sequence and the last one so long as we aren't in an option
      if (elementOpen === 0
        && lastSequence !== null
        && !(lastSequence.converter.isOption && sequence.converter.isOption)) {
        const endOfLastSequence = posDelta(lastSequence.pos)
        const startOfThisSequence = posDelta(sequence.pos)
        const text = new Text(
          Parser.unescapeText(line.substring(endOfLastSequence + 1, startOfThisSequence)),
          getActiveTextOptionData()
        )
        children.push(text)
      }

      // Call parseElement recursively on child elements
      if (sequence.converter.isElement) {
        if (sequence.isOpening) {
          if (elementOpen === 0) {
            elementSequence = sequence
          } else {
            // This is a child element of this child element, pass it along
            elementChildSequences.push(sequence)
          }
          elementOpen += 1
        } else {
          elementOpen -= 1
          if (elementOpen === 0) {
            // Get the element data. Element data does not cascade like text data.
            let data = {}
            if (elementSequence.link.option !== null) {
              data = elementSequence.link.option.data
            }

            // Create an element with no children and then recursively call this function with any text data
            // that encapsulates the entire element, a subsection of the line that's contained within the
            // element (along with the delta between the start of the subsection line and the full line) and
            // a subsection of sequences that are contained within the element
            const element = new Element(elementSequence.converter.type, [], data)
            const endOfElementStartSequence = posDelta(elementSequence.pos)
            const startOfElementEndSequence = posDelta(sequence.pos)
            element.children = this.parseInlineElement(
              line.substring(endOfElementStartSequence + 1, startOfElementEndSequence),
              elementSequence.pos + 1,
              elementChildSequences,
              activeTextOptions
            )
            children.push(element)
            elementChildSequences = []
          } else {
            // This is a child element of this child element, pass it along
            elementChildSequences.push(sequence)
          }
        }
      } else if (sequence.converter.isText) {
        if (elementOpen === 0) {
          if (sequence.isOpening) {
            if (sequence.link.option !== null) {
              activeTextOptions.push(sequence.link.option)
            }
          } else {
            if (sequence.link.option !== null) {
              activeTextOptions.pop()
            }
          }
        } else {
          // This is a child of a child element, pass it along
          elementChildSequences.push(sequence)
        }
      } else if (sequence.converter.isOption) {
        if (elementOpen > 0) {
          // This is a child of a child element, pass it along
          elementChildSequences.push(sequence)
        }
      }
      lastSequence = sequence
    })

    // Retrieve the non-formatted text at the end of the element
    if (sortedSequences && sortedSequences.length > 1) {
      const endOfLastSequence = posDelta(sortedSequences[sortedSequences.length - 1].pos)
      const endingText = new Text(
        Parser.unescapeText(line.substring(endOfLastSequence + 1)),
        getActiveTextOptionData()
      )
      children.push(endingText)
    }
    return children
  }

  parseOption(line) {
    // This parses the provided string as option data and returns the dictionary representation.
    let ret = {}
    const valuePairs = Parser.escapedSplit(line, Parser.CHAR_OPTION_PAIR_SEPARATOR)
    valuePairs.forEach(valuePair => {
      const parts = Parser.escapedSplit(valuePair, Parser.CHAR_OPTION_KEY_VALUE_SEPARATOR, true)
      if (parts && parts.length === 1) {
        ret[parts[0]] = Parser.DEFAULT_OPTION_VALUE
      } else if (parts && parts.length >= 2) {
        ret[parts[0]] = parts[1]
      }
    })
    return ret
  }

  parseOptions(line, sortedSequences) {
    // This formats all option data from its text representation to a dictionary.
    // The provided list of sorted and paired sequences are modified in place.
    let lastSequence = null
    let lastSequenceIndex = 0

    for (let i = 0; i < sortedSequences.length; i++) {
      const sequence = sortedSequences[i]
      if (sequence.converter.isOption && sequence.isOpening && lastSequence !== null) {
        const startPos = sequence.pos
        const endPos = sequence.link.closing.pos
        sortedSequences[lastSequenceIndex].link.option = new Option()
        sortedSequences[lastSequenceIndex].link.option.data = this.parseOption(line.substring(startPos + 1, endPos))
      }
      lastSequence = sequence
      lastSequenceIndex = i
    }
  }

  parseSequences(parserConfig, line) {
    // Parses a line of markup and returns a list of paired sequences (open and closing) that are sorted by their
    // position in the line. Finds and removes dangling sequences (unpaired open or closing) and invalid sequences
    // (paired sequences that are inside an option sequence where nested sequences aren't allowed).
    const sequences = this.getSequences(parserConfig, line)
    const sortedSequences = Parser.pairSequences(sequences)
    const updatedSortedSequences = Parser.filterOutSequencesInOptions(sortedSequences)
    return updatedSortedSequences
  }

  plainText(parserConfig, nodes, isFirst = false) {
    // Recursively locates all the text values and returns a string without any option data or patterns.
    let ret = []
    nodes && nodes.forEach(node => {
      if (node instanceof Element) {
        if (node.type in parserConfig.blockElementsByType) {
          // Block element
          if (!isFirst) {
            ret.push(Parser.CHAR_BLOCK)
          }
          ret.push(this.plainText(parserConfig, node.children))
          isFirst = false
        } else if (node.type in parserConfig.inlineElementsByType) {
          // Inline element
          ret.push(this.plainText(parserConfig, node.children))
        } else {
          throw new UnsupportedElementTypeError(node.type)
        }
      } else if (node instanceof Text) {
        ret.push(node.text)
      } else {
        throw new UnsupportedNodeClassError(typeof node)
      }
    })
    return ret.join('')
  }

  unparseBlockElement(parserConfig, blockElement, depth = 0, isFirst = false, parent = null) {
    // Create the markup representation of a block element node.
    const textOptionMappings = this.flattenElement(parserConfig, blockElement)
    const markupOptionByText = Parser.getMarkupOptionByText(textOptionMappings)
    const elementConverter = parserConfig.blockElementsByType[blockElement.type]
    let indent = ''
    if (parent !== null && !(parent.type in parserConfig.blockElementsByType)) {
      throw new UnableToRepresentAsMarkupError(`The block element with type "${blockElement.type}" cannot be a child of inline elements`)
    }
    if (depth > 0) {
      if (elementConverter.markupIdentifier.hasIndentChildren) {
        if (parent.type === blockElement.type) {
          indent = times(depth, () => Parser.CHAR_CHILD).join('')
        } else {
          throw new UnableToRepresentAsMarkupError(`The block element with type "${blockElement.type}" cannot be a child of the element with type ${parent.type}`)
        }
      } else {
        throw new UnableToRepresentAsMarkupError(`The block element with type "${blockElement.type}" cannot be a child of other elements`)
      }
    }

    let options = ''
    if (elementConverter.markupIdentifier.hasOptions && Object.keys(blockElement.data).length > 0) {
      options = [
        parserConfig.optionConverter.markupIdentifier.openPattern,
        this.unparseOption(parserConfig, blockElement.data),
        parserConfig.optionConverter.markupIdentifier.closePattern
      ].join('')
    }

    return [
      isFirst ? '' : Parser.CHAR_BLOCK,
      indent,
      elementConverter.markupIdentifier.openPattern,
      options,
      this.unparseChildren(parserConfig, blockElement.children, depth + 1, markupOptionByText, false, blockElement)
    ].join('')
  }

  unparseChildren(parserConfig, children, depth = 0, markupOptionByText, isFirst = false, parent = null) {
    // Recursively iterates through each element to create the markup representation.
    let ret = []
    let isFirstOfBlock = parent !== null && (parent.type in parserConfig.blockElementsByType)
    let visitedBlockElementWithParent = false
    children && children.forEach(child => {
      if (child instanceof Element) {
        if (child.type in parserConfig.blockElementsByType) {
          // Block element
          ret.push(this.unparseBlockElement(parserConfig, child, depth, isFirst, parent))
          visitedBlockElementWithParent = parent !== null
          isFirst = false
        } else if (child.type in parserConfig.inlineElementsByType) {
          // Inline element
          if (visitedBlockElementWithParent) {
            throw new UnableToRepresentAsMarkupError('An inline element cannot follow a child block element')
          }
          if (parent === null) {
            throw new UnableToRepresentAsMarkupError('An inline element cannot be a root node')
          }
          ret.push(this.unparseInlineElement(parserConfig, child, markupOptionByText))
        } else {
          throw new UnsupportedElementTypeError(child.type)
        }
      } else if (child instanceof Text) {
        if (visitedBlockElementWithParent) {
          throw new UnableToRepresentAsMarkupError('A text node cannot follow a child block element')
        }
        if (parent === null) {
          throw new UnableToRepresentAsMarkupError('A text node cannot be a root node')
        }
        ret.push(this.unparseText(parserConfig, child, markupOptionByText, isFirstOfBlock))
        isFirstOfBlock = false
      } else {
        throw new UnsupportedNodeClassError(typeof node)
      }
    })

    return ret.join('')
  }

  unparseInlineElement(parserConfig, inlineElement, markupOptionByText) {
    // Create the markup representation of an inline element node.
    const elementConverter = parserConfig.inlineElementsByType[inlineElement.type]
    let options = ''
    if (elementConverter.markupIdentifier.hasOptions && Object.keys(inlineElement.data).length > 0) {
      options = [
        parserConfig.optionConverter.markupIdentifier.openPattern,
        this.unparseOption(parserConfig, inlineElement.data),
        parserConfig.optionConverter.markupIdentifier.closePattern
      ].join('')
    }

    return [
      elementConverter.markupIdentifier.openPattern,
      this.unparseChildren(parserConfig, inlineElement.children, 0, markupOptionByText, false, inlineElement),
      elementConverter.markupIdentifier.closePattern,
      options
    ].join('')
  }

  unparseOption(parserConfig, optionData) {
    // Create the markup representation of option data.
    let pairs = []
    const charsToEscape = [
      Parser.CHAR_BLOCK,
      Parser.CHAR_OPTION_KEY_VALUE_SEPARATOR,
      Parser.CHAR_OPTION_PAIR_SEPARATOR,
      parserConfig.optionConverter.markupIdentifier.openPattern,
      parserConfig.optionConverter.markupIdentifier.closePattern,
      ...Object.keys(parserConfig.inlineElementsByOpening),
      ...Object.keys(parserConfig.inlineElementsByClosing)
    ]
    for (let optionKey in optionData) {
      const optionValue = optionData[optionKey]
      pairs.push([
        Parser.escapeText(optionKey, charsToEscape),
        optionValue === Parser.DEFAULT_OPTION_VALUE ? '' : Parser.CHAR_OPTION_KEY_VALUE_SEPARATOR,
        optionValue === Parser.DEFAULT_OPTION_VALUE ? '' : Parser.escapeText(optionValue, charsToEscape)
      ].join(''))
    }
    pairs.sort()
    return pairs.join(Parser.CHAR_OPTION_PAIR_SEPARATOR)
  }

  unparseText(parserConfig, text, markupOptionByText, isFirstOfBlock = false) {
    // Create the markup representation of a text node.
    // Determine options
    let before = ''
    let options = ''
    if (markupOptionByText.has(text)) {
      const beforeNum = markupOptionByText.get(text).optionBeginnings.length
      before = times(beforeNum, () => parserConfig.textConverter.markupIdentifier.openPattern).join('')

      let tempOptions = []
      while (markupOptionByText.get(text).optionEndings.length > 0) {
        const optionEnding = markupOptionByText.get(text).optionEndings.pop()
        tempOptions.push([
          parserConfig.textConverter.markupIdentifier.closePattern,
          parserConfig.optionConverter.markupIdentifier.openPattern,
          this.unparseOption(parserConfig, optionEnding.data),
          parserConfig.optionConverter.markupIdentifier.closePattern
        ].join(''))
      }
      options = tempOptions.join('')
    }

    // Create escape sets
    const charsToEscape = [
      Parser.CHAR_BLOCK,
      parserConfig.optionConverter.markupIdentifier.openPattern,
      parserConfig.optionConverter.markupIdentifier.closePattern,
      parserConfig.textConverter.markupIdentifier.openPattern,
      parserConfig.textConverter.markupIdentifier.closePattern,
      ...Object.keys(parserConfig.inlineElementsByOpening),
      ...Object.keys(parserConfig.inlineElementsByClosing)
    ]
    // We escape the first character differently simply to avoid having to escape block element open patterns
    // when it isn't necessary #savethosecharacters
    const charsToEscapeFirst = isFirstOfBlock ? [
      Parser.CHAR_CHILD,
      ...Object.keys(parserConfig.blockElementsByOpening),
      ...charsToEscape
    ] : [ ...charsToEscape ]

    return [
      before,
      Parser.escapeText(text.text.substring(0, 1), charsToEscapeFirst),
      Parser.escapeText(text.text.substring(1), charsToEscape),
      options
    ].join('')
  }
}

Parser.BLANK_TEXT = new Text('')
Parser.CHAR_BLOCK = '\n'
Parser.CHAR_CHILD = '\t'
Parser.CHAR_ESCAPE = '\\'
Parser.CHAR_OPTION_KEY_VALUE_SEPARATOR = ':'
Parser.CHAR_OPTION_PAIR_SEPARATOR = ';'
Parser.DEFAULT_OPTION_VALUE = 'true'
Parser.DOUBLE_ESCAPE = '\\\\'

export default Parser